import React, { lazy } from 'react';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import { useLocalDebouncedTheme } from '../../hooks/useSetThemeAttribute.js';
import InputSkeleton from '../../../../skeleton_loaders/common/InputSkeleton.jsx';
import ButtonSkeleton from '../../../../skeleton_loaders/common/ButtonSkeleton.jsx';
import withSuspense from '../../../../helpers/withSuspense.jsx';
import ColorPicker from '../../../common/ColorPicker/DashboardColorPicker.jsx';
import { DEFAULT_FONT_FAMILY, FONT_FAMILY_TYPE } from '../../gen2_widgets/schemaHelpers.js';
import RepeatableColorPicker from '../../../common/ColorPicker/DashboardRepeatableColorPicker.jsx';
import styles from './DashboardThemeEditor.module.scss';
import TooltipWithRef from '../../../common/Tooltip.jsx';
import SettingsSection from '../common/SettingsSection.jsx';
import CommonSlider from '../../../common/CommonSlider.jsx';

const Input = withSuspense(lazy(() => import('./LabeledInput.jsx')), InputSkeleton);
const Select = withSuspense(lazy(() => import('../../../common/Select.jsx')), InputSkeleton);
const ToggleSwitch = withSuspense(lazy(() => import('../../../common/ToggleSwitch.jsx')), <ButtonSkeleton buttonContent='--' size='xs' className='mb-1'  />);

function DashboardThemeEditor() {
  const [theme, setAttribute] = useLocalDebouncedTheme();

  const title = theme.overrides?.common?.title || {};
  const padding = theme.overrides?.common?.padding || {};
  const titleEnabled = title.enabled != null ? title.enabled : true;

  return (
    <div className={styles['theme-editor']}>
      <div>
        <CollapsibleSection initialCollapsed={true} title={I18n.t('dashboard_layout')}>
          <SettingsSection>
            <Input label={I18n.t('gap')}>
              <CommonSlider value={theme.background?.gap} max={50} onChange={(value) => setAttribute('background.gap', value)} defaultValue={5} />
            </Input>
            <Input label={I18n.t('outer_padding')}>
              <CommonSlider value={theme.background?.padding} max={50} onChange={(value) => setAttribute('background.padding', value)} defaultValue={0} />
            </Input>
            <Input label={I18n.t('background_color')}>
              <ColorPicker value={theme.background?.backgroundColor} onChange={(value) => setAttribute('background.backgroundColor', value)} defaultValue='#FDFDFD' />
            </Input>
          </SettingsSection>
        </CollapsibleSection>
        <hr />
        <CollapsibleSection initialCollapsed={true} title={I18n.t('color_palette')}>
          <SettingsSection>
            <Input label={I18n.t('fills')}>
              <RepeatableColorPicker value={theme.palette?.fills} onChange={(value) => setAttribute('palette.fills', value.length ? value : null)} />
            </Input>
            <Input label={I18n.t('strokes')}>
              <RepeatableColorPicker value={theme.palette?.strokes} onChange={(value) => setAttribute('palette.strokes', value.length ? value : null)} />
            </Input>
          </SettingsSection>
        </CollapsibleSection>
        <hr />
        <CollapsibleSection initialCollapsed={true} title={I18n.t('widget_layout')}>
          <SettingsSection>
            <Input label={I18n.t('base_font_size')}>
              <CommonSlider value={theme.params?.fontSize} min={8} max={50} onChange={(value) => setAttribute('params.fontSize', value)} defaultValue={12} />
            </Input>
            <Input label={I18n.t('text_color')}>
              <ColorPicker value={theme.params?.textColor} onChange={(value) => setAttribute('params.textColor', value)} defaultValue='#000000' />
            </Input>
            <Input label={I18n.t('font_family')}>
              <Select
                options={FONT_FAMILY_TYPE.enum.map((o) => ({value: o, label: o}))}
                defaultValue={DEFAULT_FONT_FAMILY}
                value={theme.params?.fontFamily}
                onChange={(value) => setAttribute('params.fontFamily', value)}
              />
            </Input>
            <Input label={I18n.t('accent_color')}>
              <ColorPicker value={theme.params?.accentColor} onChange={(value) => setAttribute('params.accentColor', value)} />
            </Input>
            <Input label={I18n.t('border_radius')}>
              <CommonSlider value={theme.params?.borderRadius} max={50} onChange={(value) => setAttribute('params.borderRadius', value)} defaultValue={3} />
            </Input>
            <Input label={I18n.t('border_width')}>
              <CommonSlider value={theme.params?.borderWidth} min={1} max={5} onChange={(value) => setAttribute('params.borderWidth', value)} defaultValue={1} />
            </Input>
            <Input label={I18n.t('background_color')}>
              <ColorPicker value={theme.params?.backgroundColor} onChange={(value) => setAttribute('params.backgroundColor', value)} defaultValue='#FFFFFF' />
            </Input>
            <Input label={I18n.t('foreground_color')}>
              <ColorPicker value={theme.params?.foregroundColor} onChange={(value) => setAttribute('params.foregroundColor', value)} defaultValue='#181D1E' />
            </Input>
            <br />
            <Input label={I18n.t('padding_top')}>
              <CommonSlider value={padding.top} min={0} max={50} onChange={(value) => setAttribute('overrides.common.padding.top', value)} defaultValue={10} />
            </Input>
            <Input label={I18n.t('padding_left')}>
              <CommonSlider value={padding.left} min={0} max={50} onChange={(value) => setAttribute('overrides.common.padding.left', value)} defaultValue={10} />
            </Input>
            <Input label={I18n.t('padding_right')}>
              <CommonSlider value={padding.right} min={0} max={50} onChange={(value) => setAttribute('overrides.common.padding.right', value)} defaultValue={10} />
            </Input>
            <Input label={I18n.t('padding_bottom')}>
              <CommonSlider value={padding.bottom} min={0} max={50} onChange={(value) => setAttribute('overrides.common.padding.bottom', value)} defaultValue={10} />
            </Input>
          </SettingsSection>
        </CollapsibleSection>
        <hr />
        <CollapsibleSection initialCollapsed={true} title={I18n.t('widget_titles')} buttons={
          <TooltipWithRef title={I18n.t('collapse_section_toggle_tooltip')} trigger='hover'>
            <ToggleSwitch size='sm' name='widget-titles' className={styles['toggle-collapsible-section']} isOn={titleEnabled} handleToggle={() => setAttribute('overrides.common.title.enabled', !titleEnabled)} />
          </TooltipWithRef>}
        >
          <SettingsSection>
            <Input label={I18n.t('title_color')}>
              <ColorPicker value={title.color} onChange={(value) => setAttribute('overrides.common.title.color', value)} defaultValue='#2D2D31' />
            </Input>
            <Input label={I18n.t('spacing')}>
              <CommonSlider value={title.spacing} max={50} onChange={(value) => setAttribute('overrides.common.title.spacing', value)} />
            </Input>
            <Input label={I18n.t('font_size')}>
              <CommonSlider value={title.fontSize} min={8} max={50} onChange={(value) => setAttribute('overrides.common.title.fontSize', value)} defaultValue={16} />
            </Input>
            <Input label={I18n.t('font_family')}>
              <Select
                defaultValue={DEFAULT_FONT_FAMILY}
                options={FONT_FAMILY_TYPE.enum.map((o) => ({value: o, label: o}))}
                value={title.fontFamily}
                onChange={(value) => setAttribute('overrides.common.title.fontFamily', value)}
              />
            </Input>
          </SettingsSection>
        </CollapsibleSection>
      </div>
    </div>
  )
}

export default DashboardThemeEditor;